import { cn } from '../cn.js';

/**
 * Logo MiniSOC v4 (docs/init/minisoc-v4-bleu.svg), reproduit en SVG inline.
 *
 * Le fichier source est dessiné pour un fond clair (« mini » en Deep Void).
 * Ici le mot « mini » hérite de `currentColor` pour rester lisible sur les
 * surfaces sombres des deux fronts, tandis que « soc » et le bouclier gardent
 * le bleu électrique de la charte. Les tracés sont ceux du fichier d'origine :
 * ne pas les retoucher à la main, régénérer depuis la source si le logo évolue.
 *
 * La hauteur se pilote par classe (`h-7` par défaut), la largeur suit le ratio
 * du viewBox (environ 4.7:1).
 */
export function LogoWordmark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="120 125 3470 745"
      className={cn('h-7 w-auto text-text', className)}
      role="img"
      aria-label="MiniSOC"
    >
      <g transform="translate(70,830) scale(1,-1)">
        {/* « mini » */}
        <path
          fill="currentColor"
          d="M63.375 0V477.5H163.5V0ZM383.5 0V286.5Q383.5 337 352.25 365.4375Q321 393.875 274.625 393.875Q243.125 393.875 218.1875 381.0Q193.25 368.125 178.375 344.25Q163.5 320.375 163.5 286.75L123.25 307.875Q123.25 362.625 147.1875 402.625Q171.125 442.625 212.0625 465.0625Q253 487.5 303.875 487.5Q354.625 487.5 395.3125 465.5Q436 443.5 459.8125 403.5Q483.625 363.5 483.625 307.75V0ZM703.625 0V286.5Q703.625 337 672.375 365.4375Q641.125 393.875 594.75 393.875Q564.125 393.875 538.75 381.0Q513.375 368.125 498.5 344.25Q483.625 320.375 483.625 286.75L426.75 307.875Q431.125 362.75 457.75 402.6875Q484.375 442.625 526.625 465.0625Q568.875 487.5 619.625 487.5Q671.25 487.5 712.8125 465.5Q754.375 443.5 779.0625 403.4375Q803.75 363.375 803.75 307.625V0Z"
        />
        <path
          fill="currentColor"
          transform="translate(870,0)"
          d="M64.25 0V477.5H164.375V0ZM114.25 568.875Q88.125 568.875 70.8125 586.75Q53.5 604.625 53.5 630.75Q53.5 657 70.8125 674.75Q88.125 692.5 114.25 692.5Q141.375 692.5 158.3125 674.75Q175.25 657 175.25 630.75Q175.25 604.625 158.3125 586.75Q141.375 568.875 114.25 568.875Z"
        />
        <path
          fill="currentColor"
          transform="translate(1111,0)"
          d="M395.75 0V276.75Q395.75 328.125 363.6875 361.0Q331.625 393.875 280.8392857142857 393.875Q246.27678571428572 393.875 219.82589285714286 378.9375Q193.375 364 178.4375 337.5Q163.5 311 163.5 276.875L123.25 299.375Q123.25 354 147.4375 396.3125Q171.625 438.625 214.1589467005076 463.0625Q256.6928934010152 487.5 310.4714467005076 487.5Q364.25 487.5 406.1875 460.3125Q448.125 433.125 472.0 389.5625Q495.875 346 495.875 297.75V0ZM63.375 0V477.5H163.5V0Z"
        />
        <path
          fill="currentColor"
          transform="translate(1672,0)"
          d="M64.25 0V477.5H164.375V0ZM114.25 568.875Q88.125 568.875 70.8125 586.75Q53.5 604.625 53.5 630.75Q53.5 657 70.8125 674.75Q88.125 692.5 114.25 692.5Q141.375 692.5 158.3125 674.75Q175.25 657 175.25 630.75Q175.25 604.625 158.3125 586.75Q141.375 568.875 114.25 568.875Z"
        />

        {/* « soc » : le « o » est le bouclier de la marque */}
        <g fill="var(--color-brand-500)">
          <path
            transform="translate(1943,0)"
            d="M236.75 -13.75Q192.25 -13.75 148.5 -2.125Q104.75 9.5 67.375 30.125Q30 50.75 4 78L98.75 175Q123 149.75 156.25 135.625Q189.5 121.5 227.5 121.5Q249.5 121.5 261.375 127.625Q273.25 133.75 273.25 145Q273.25 161 256.375 169.25Q239.5 177.5 213.25 184.125Q187 190.75 157.75 200.125Q128.5 209.5 101.875 225.75Q75.25 242 58.75 270.875Q42.25 299.75 42.25 345Q42.25 391.75 67.5 427.375Q92.75 463 139.0 483.625Q185.25 504.25 247 504.25Q310.25 504.25 367.125 482.625Q424 461 458.5 417.75L363 320.75Q339 348.25 309.75 358.625Q280.5 369 256.75 369Q234.25 369 224.125 362.375Q214 355.75 214 345Q214 331.75 230.5 323.75Q247 315.75 273.25 309.25Q299.5 302.75 328.375 292.875Q357.25 283 383.5 265.375Q409.75 247.75 426.25 218.75Q442.75 189.75 442.75 143.5Q442.75 71.25 387.0 28.75Q331.25 -13.75 236.75 -13.75Z"
          />
          <circle cx="2717" cy="244.5" r="272.875" />
          <path
            transform="translate(3020,0)"
            d="M292.5 -11.5Q214.75 -11.5 152.375 21.5Q90 54.5 54.0 112.625Q18 170.75 18 243.75Q18 317.75 54.375 375.75Q90.75 433.75 153.5 467.125Q216.25 500.5 294.75 500.5Q352.25 500.5 399.25 481.625Q446.25 462.75 484.75 425L374.5 314.5Q359.5 329.75 340.125 337.375Q320.75 345 294.75 345Q266.5 345 244.0 332.5Q221.5 320 208.125 297.75Q194.75 275.5 194.75 245.25Q194.75 215.25 208.125 192.5Q221.5 169.75 244.375 156.875Q267.25 144 294.75 144Q323 144 343.125 153.125Q363.25 162.25 378.25 179L488.5 68.5Q447.75 28.5 400.375 8.5Q353 -11.5 292.5 -11.5Z"
          />
        </g>
        <path
          d="M2717.0 408.225 C2670.010925 362.382 2620.33676 346.0095 2582.7455 346.0095 L2582.7455 236.31375 C2582.7455 154.45125000000002 2643.160025 105.33375000000001 2717.0 80.775 C2790.839975 105.33375000000001 2851.2545 154.45125000000002 2851.2545 236.31375 L2851.2545 346.0095 C2754.59126 346.0095 2763.989075 362.382 2717.0 408.225 Z"
          fill="none"
          stroke="#ffffff"
          strokeWidth="43.66"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

/**
 * Logo avec sous-titre optionnel (« Backoffice »), décliné dans les barres
 * latérales et les pages de connexion des deux fronts.
 */
export function Logo({ subtitle, className }: { subtitle?: string; className?: string }) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <LogoWordmark className={className} />
      {subtitle ? (
        <span className="block w-fit rounded bg-violet/15 px-1.5 py-0.5 font-mono text-[0.625rem] font-bold uppercase tracking-widest text-violet">
          {subtitle}
        </span>
      ) : null}
    </div>
  );
}
